import React, { useState } from 'react';
import { Edit3, User, Clock, CheckCircle, FileText, Building, Calendar, MapPin, Award, Users, Target, ChevronDown, ChevronUp, X, Save } from 'lucide-react';
import html2pdf from 'html2pdf.js'; 
import { useRef } from 'react';
import htmlDocx from 'html-docx-js';
import { saveAs } from 'file-saver';


const ProfileScreen = ({
  profileData,
  profileSummary,
  profileLoading,
  sessionLoading,
  isEditModalOpen,
  fieldToEdit,
  editValue,
  onProfileConfirm,
  onEditField,
  setIsEditModalOpen,
  setEditValue,
  setProfileData
}) => {
  const [showFullSummary, setShowFullSummary] = useState(true);

  // Handle opening edit modal
  const handleEditClick = (field: string, currentValue: string) => {
    onEditField(field, currentValue);
    setEditValue(currentValue);
    setIsEditModalOpen(true);
  };
 const summaryRef = useRef<HTMLDivElement>(null);

const handleExportToPDF = () => {
  const original = summaryRef.current;
  if (!original) return;

  // Clone the element
  const clone = original.cloneNode(true) as HTMLElement;

  // Clean up scroll styles
  clone.style.maxHeight = 'none';
  clone.style.overflow = 'visible';

  // Optional: Add a class to help with PDF styles
  clone.classList.add('html2pdf-content');

  // Append clone temporarily to the DOM (so it's rendered)
  document.body.appendChild(clone);

  // Export the clone
  const opt = {
    margin: 0.5,
    filename: 'profile-summary.pdf',
    image: { type: 'jpeg', quality: 0.98 },
    html2canvas: { scale: 2, useCORS: true },
    jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' },
    pagebreak: { mode: ['avoid-all', 'css', 'legacy'] },
  };

  html2pdf().set(opt).from(clone).save().then(() => {
    // Remove the clone after export
    document.body.removeChild(clone);
  });
};


// Handle saving changes
  const handleSaveChanges = () => {
    if (!fieldToEdit) return;

    const updatedData = { ...profileData };
    
    // Handle Employee Overview fields
    if (fieldToEdit.toLowerCase().includes('name') || fieldToEdit === 'Name') {
      updatedData.name = editValue;
    } else if (fieldToEdit.toLowerCase().includes('role') || fieldToEdit === 'Role Title') {
      updatedData.role = editValue;
    } else if (fieldToEdit.toLowerCase().includes('tenure') || fieldToEdit === 'Tenure') {
      updatedData.tenure = editValue;
    } else if (fieldToEdit.toLowerCase().includes('location') || fieldToEdit === 'Primary Location') {
      updatedData.location = editValue;
    } else if (fieldToEdit.toLowerCase().includes('business') || fieldToEdit === 'Business Unit') {
      updatedData.businessUnit = editValue;
    } else if (fieldToEdit.toLowerCase().includes('working') || fieldToEdit === 'Last Working Day') {
      updatedData.lastWorkingDay = editValue;
    } else if (fieldToEdit.toLowerCase().includes('employee') || fieldToEdit === 'Employee ID') {
      updatedData.employeeId = editValue;
    } else if (fieldToEdit.toLowerCase().includes('core tasks')) {
      updatedData.coreTasks = editValue.split('\n').filter(task => task.trim());
    } else {
      // Handle other table fields by storing them in a custom fields object
      if (!updatedData.customFields) {
        updatedData.customFields = {};
      }
      updatedData.customFields[fieldToEdit] = editValue;
    }

    setProfileData(updatedData);
    setIsEditModalOpen(false);
    setEditValue('');
  };

  // Handle closing modal
  const handleCloseModal = () => {
    setIsEditModalOpen(false);
    setEditValue('');
  };

  // Format markdown content for better display
  const formatSummaryContent = (content: string) => {
    const sections = content.split('\n\n');
    return sections.map((section, index) => {
      if (section.startsWith('# ')) {
        return <h1 key={index} className="text-2xl font-bold text-gray-900 mb-4">{section.replace('# ', '')}</h1>;
      } else if (section.startsWith('## ')) {
        return <h2 key={index} className="text-xl font-semibold text-gray-800 mb-3 mt-6">{section.replace('## ', '')}</h2>;
      } else if (section.startsWith('| ')) {
        // Format table with edit buttons for all tables
        const rows = section.split('\n').filter(row => row.trim());
        const isEmployeeOverview = section.includes('Employee Overview') || 
                                   section.includes('Name') || 
                                   section.includes('Role Title');
        
        return (
          <div key={index} className="overflow-x-auto mb-4">
            <table className="min-w-full border border-gray-300 rounded-lg">
              <tbody>
                {rows.map((row, rowIndex) => {
                  if (row.includes('|---')) return null;
                  const cells = row.split('|').map(cell => cell.trim()).filter(cell => cell);
                  
                  // Handle header row
                  if (rowIndex === 0) {
                    return (
                      <tr key={rowIndex} className="bg-gray-50">
                        {cells.map((cell, cellIndex) => (
                          <td key={cellIndex} className="px-4 py-2 border border-gray-200 font-semibold text-gray-700">
                            {cell}
                          </td>
                        ))}
                        {/* Only add Actions column for Employee Overview table */}
                        {isEmployeeOverview && (
                          <td className="px-4 py-2 border border-gray-200 font-semibold text-gray-700">
                            Actions
                          </td>
                        )}
                      </tr>
                    );
                  }

                  // For Employee Overview table, show edit buttons in Actions column
                  if (isEmployeeOverview) {
                    return (
                      <tr key={rowIndex} className="bg-white hover:bg-gray-50">
                        {cells.map((cell, cellIndex) => (
                          <td key={cellIndex} className="px-4 py-2 border border-gray-200 text-gray-600">
                            {cellIndex === 1 ? getFieldValue(cells[0]) : cell}
                          </td>
                        ))}
                        <td className="px-4 py-2 border border-gray-200">
                          <button
                            onClick={() => handleEditClick(cells[0], getFieldValue(cells[0]))}
                            className="flex items-center gap-1 px-2 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors text-sm"
                          >
                            <Edit3 className="w-3 h-3" />
                            Edit
                          </button>
                        </td>
                      </tr>
                    );
                  }

                  // For other tables (Functional Knowledge, Tools, Projects), add edit buttons to each cell
                  return (
                    <tr key={rowIndex} className="bg-white hover:bg-gray-50">
                      {cells.map((cell, cellIndex) => (
                        <td key={cellIndex} className="px-4 py-2 border border-gray-200 text-gray-600 relative group">
                          <div className="flex items-center justify-between">
                            <span className="flex-1">
                              {getCustomFieldValue(`${getSectionTitle(section)}_${cells[0]}_${cellIndex}`, cell)}
                            </span>
                            {/* Add edit button for value cells (not the first column which is usually the label) */}
                            {cellIndex > 0 && (
                              <button
                                onClick={() => handleEditClick(
                                  `${getSectionTitle(section)}_${cells[0]}_${cellIndex}`, 
                                  getCustomFieldValue(`${getSectionTitle(section)}_${cells[0]}_${cellIndex}`, cell)
                                )}
                                className="opacity-0 group-hover:opacity-100 ml-2 p-1 bg-blue-500 text-white rounded hover:bg-blue-600 transition-all text-xs flex-shrink-0"
                                title="Edit this field"
                              >
                                <Edit3 className="w-3 h-3" />
                              </button>
                            )}
                          </div>
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        );
      } else if (section.startsWith('- **')) {
        // Format bullet points with bold headers
        const items = section.split('\n').filter(item => item.trim());
        return (
          <ul key={index} className="space-y-2 mb-4">
            {items.map((item, itemIndex) => {
              const match = item.match(/- \*\*(.*?):\*\* (.*)/);
              if (match) {
                return (
                  <li key={itemIndex} className="flex">
                    <span className="font-semibold text-gray-800 mr-2">{match[1]}:</span>
                    <span className="text-gray-600">{match[2]}</span>
                  </li>
                );
              }
              return <li key={itemIndex} className="text-gray-600">{item.replace('- ', '')}</li>;
            })}
          </ul>
        );
      } else if (section.includes('---')) {
        return <hr key={index} className="my-6 border-gray-300" />;
      } else if (section.trim()) {
        return <p key={index} className="text-gray-700 leading-relaxed mb-4">{section}</p>;
      }
      return null;
    }).filter(Boolean);
  };

  // Parse the markdown summary to extract key information
  const parseProfileSummary = (summary: string) => {
    if (!summary) return null;

    const lines = summary.split('\n');
    const sections: { [key: string]: string[] } = {};
    let currentSection = '';

    lines.forEach(line => {
      if (line.startsWith('##')) {
        currentSection = line.replace('##', '').trim();
        sections[currentSection] = [];
      } else if (currentSection && line.trim()) {
        sections[currentSection].push(line.trim());
      }
    });

    return sections;
  };

  const summaryData = profileSummary ? parseProfileSummary(profileSummary.executive_summary) : null;

  // Extract key metrics from the summary
  const extractKeyMetrics = (sections: any) => {
    if (!sections) return null;

    const overview = sections['1. Employee Overview'] || [];
    const skills = sections['2. Key Skills and Expertise'] || [];
    const responsibilities = sections['4. Key Responsibilities and Contributions'] || [];

    return {
      name: overview.find(line => line.includes('Name'))?.split('|')[2]?.trim() || 'Gaurav Agarwal',
      role: overview.find(line => line.includes('Role Title'))?.split('|')[2]?.trim() || 'Functional Consultant - Finance Analytics',
      tenure: overview.find(line => line.includes('Tenure'))?.split('|')[2]?.trim() || '4 years 3 months',
      location: overview.find(line => line.includes('Primary Location'))?.split('|')[2]?.trim() || 'Delhi (IST)',
      businessUnit: overview.find(line => line.includes('Business Unit'))?.split('|')[2]?.trim() || 'Global Finance Centre of Excellence',
      lastWorkingDay: overview.find(line => line.includes('Last Working Day'))?.split('|')[2]?.trim() || '15 June 2025',
      employeeId: overview.find(line => line.includes('Employee ID'))?.split('|')[2]?.trim() || '87214',
      keySkills: skills.filter(line => line.startsWith('- **')).map(line => line.replace('- **', '').split(':**')[0]),
      responsibilities: responsibilities.filter(line => line.startsWith('- ')).map(line => line.replace('- ', ''))
    };
  };

  // Merge AI extracted data with user-edited data, prioritizing edited data
  const baseMetrics = summaryData ? extractKeyMetrics(summaryData) : null;
  const metrics = baseMetrics ? {
    ...baseMetrics,
    name: profileData.name || baseMetrics.name,
    role: profileData.role || baseMetrics.role,
    tenure: profileData.tenure || baseMetrics.tenure,
    location: profileData.location || baseMetrics.location,
    businessUnit: profileData.businessUnit || baseMetrics.businessUnit,
    lastWorkingDay: profileData.lastWorkingDay || baseMetrics.lastWorkingDay,
    employeeId: profileData.employeeId || baseMetrics.employeeId,
  } : null;

  // Get field value for editing
  const getFieldValue = (fieldName: string) => {
    if (!metrics) return '';
    
    switch (fieldName.toLowerCase()) {
      case 'name':
        return metrics.name;
      case 'role title':
        return metrics.role;
      case 'tenure':
        return metrics.tenure;
      case 'primary location':
        return metrics.location;
      case 'business unit':
        return metrics.businessUnit;
      case 'last working day':
        return metrics.lastWorkingDay;
      case 'employee id':
        return metrics.employeeId;
      default:
        return '';
    }
  };

  // Get custom field value (for non-Employee Overview tables)
  const getCustomFieldValue = (fieldKey: string, defaultValue: string) => {
    return profileData.customFields?.[fieldKey] || defaultValue;
  };

  // Extract section title from markdown section
  const getSectionTitle = (section: string) => {
    const firstLine = section.split('\n')[0];
    // Remove markdown table syntax and get clean title
    return firstLine.replace(/\|/g, '').trim().replace(/\s+/g, '_');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 welcome-screen">
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 mx-auto">
        {/* Header */}
        <div className="mb-1">
          <div style={{justifyContent:"space-between", alignContent:"flex-start",alignItems:"flex-start"}} className="flex gap-4 ">
           
            <div className='flex items-center gap-4 mb-4'>
            <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center shadow-lg">
              <User className="w-4 h-4 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Profile Confirmation
              </h1>
              <p className="text-gray-600 text-sm">
                Review your profile information before starting the interview
              </p>
            </div>

            </div>

            <div>
               {!profileLoading && (
          <div className="flex justify-center   border-gray-200">
                <button
      onClick={handleExportToPDF} // Replace with your actual export function
                    style={{ background: "linear-gradient(135deg, #bb1ccc 0%, #d946ef 100%)" ,fontSize:"14px" ,marginRight:'1rem'}}
              className="text-white px-4 py-3 rounded-xl font-semibold hover:opacity-90 transition-all duration-200 shadow-lg transform hover:scale-105 text-lg"
    >
      Export to PDF
    </button>
            <button
              onClick={onProfileConfirm}
              style={{ background: "linear-gradient(135deg, #bb1ccc 0%, #d946ef 100%)" ,fontSize:"14px"}}
              className="text-white px-4 py-3 rounded-xl font-semibold hover:opacity-90 transition-all duration-200 shadow-lg transform hover:scale-105 text-lg"
              disabled={sessionLoading}
              
            >
              {sessionLoading ? (
                <>
                  <div style={{fontSize:"14px"}} className="border-2 border-white border-t-transparent rounded-full animate-spin inline-block mr-3"></div>
                  Starting Interview...
                </>
              ) : (
                "Confirm Profile & Start Interview"
              )}
            </button>
          </div>
        )}
            </div>
           
          </div>
        </div>

        {profileLoading ? (
          <div style={{minHeight:"800px",alignContent:"center",alignItems:"center"}} className="text-center py-16">
            <div className="w-12 h-12 border-4 border-purple-600 border-t-transparent rounded-full animate-spin mx-auto mb-6"></div>
            <p className="text-gray-600 text-lg">Loading your profile summary...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-1 gap-8">
            {/* Main Content - Detailed Profile */}
            <div className="lg:col-span-3">
              {/* AI Generated Summary */}
              {profileSummary && (
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 mb-8 border border-blue-200">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-semibold text-blue-900 flex items-center gap-2">
                      <FileText className="w-5 h-5" />
                      AI-Generated Profile Summary
                      <span className="text-sm font-normal text-blue-600 ml-2">(Click Edit buttons to modify)</span>
                    </h2>
                    <button
                      onClick={() => setShowFullSummary(!showFullSummary)}
                      className="flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors text-sm"
                    >
                      {showFullSummary ? (
                        <>
                          <ChevronUp className="w-4 h-4" />
                          Show Less
                        </>
                      ) : (
                        <>
                          <ChevronDown className="w-4 h-4" />
                          Show Full Summary
                        </>
                      )}
                    </button>
                  </div>

                  {showFullSummary ? (
                    <div  ref={summaryRef} style={{maxHeight:"800px"}} className="bg-white rounded-lg p-6 overflow-y-auto shadow-inner">
                      <div className="prose prose-sm max-w-none">
                        {formatSummaryContent(profileSummary.executive_summary)}
                      </div>
                    </div>
                  ) : (
                    <div className="bg-white rounded-lg p-4 shadow-sm">
                      <p className="text-gray-700 leading-relaxed">
                        {metrics ? (
                          `${metrics.name} is a ${metrics.role} with ${metrics.tenure} of experience at ${metrics.businessUnit}. 
                          Specializing in finance analytics and consulting, with expertise in bridging business and analytics teams.`
                        ) : (
                          "AI-generated profile summary is being processed..."
                        )}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Core Tasks Section */}
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-green-900 flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    Core Tasks
                  </h3>
                  <button
                    onClick={() => handleEditClick('Core Tasks', profileData.coreTasks.join('\n'))}
                    className="flex items-center gap-1 px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600 transition-colors text-sm"
                  >
                    <Edit3 className="w-4 h-4" />
                    Edit Tasks
                  </button>
                </div>
                <ul className="space-y-2">
                  {profileData.coreTasks.map((task, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle className="w-4 h-4 text-green-600 mt-1 flex-shrink-0" />
                      <span className="text-gray-700 leading-relaxed text-sm">{task}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Edit Modal */}
        {isEditModalOpen && (
          <div style={{zIndex:"99999"}} className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4 shadow-2xl">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Edit {fieldToEdit}
                </h3>
                <button
                  onClick={handleCloseModal}
                  className="p-1 hover:bg-gray-100 rounded"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {fieldToEdit}
                </label>
                {fieldToEdit?.toLowerCase() === 'core tasks' ? (
                  <textarea
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    rows={6}
                    placeholder="Enter each task on a new line"
                  />
                ) : (
                  <input
                    type="text"
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={`Enter ${fieldToEdit?.toLowerCase()}`}
                  />
                )}
              </div>

              <div className="flex gap-3 justify-end">
                <button
                  onClick={handleCloseModal}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveChanges}
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfileScreen;